/**
 * Chain + app configuration. Every address/URL is configurable via Vite env
 * vars (see .env.example); the defaults below are the values verified
 * 2026-07-22. Nothing here is imported into business logic as a literal —
 * pages and components read from this single module.
 */

const env = import.meta.env

export const MOCK_MODE = env.VITE_MOCK === '1'

export const CHAIN = {
  mainnet: {
    chainId: Number(env.VITE_CHAIN_ID ?? 4663),
    rpcUrl: env.VITE_RPC_URL ?? 'https://rpc.mainnet.chain.robinhood.com',
    explorerUrl:
      env.VITE_EXPLORER_URL ?? 'https://robinhoodchain.blockscout.com',
    gasSymbol: 'ETH',
  },
  testnet: {
    chainId: Number(env.VITE_TESTNET_CHAIN_ID ?? 46630),
    rpcUrl:
      env.VITE_TESTNET_RPC_URL ?? 'https://rpc.testnet.chain.robinhood.com',
    explorerUrl:
      env.VITE_TESTNET_EXPLORER_URL ??
      'https://explorer.testnet.chain.robinhood.com',
    faucetUrl: 'https://faucet.testnet.chain.robinhood.com',
    gasSymbol: 'ETH',
  },
} as const

export const ADDRESSES = {
  /** USDG — cash asset (mainnet) */
  usdg:
    env.VITE_USDG_ADDRESS ?? '0x5fc5360D0400a0Fd4f2af552ADD042D716F1d168',
  /** Wrapped ETH (mainnet) */
  weth:
    env.VITE_WETH_ADDRESS ?? '0x0Bd7D308f8E1639FAb988df18A8011f41EAcAD73',
  /** StonkBrokers NFT collection (mainnet, reference) */
  stonkBrokersNft:
    env.VITE_STONKBROKERS_NFT ?? '0x539CdD042c2f3d93EbC5BE7DfFf0c79F3B4fAbF0',
  /** ERC-6551 registry clone (mainnet, reference) */
  erc6551Registry:
    env.VITE_ERC6551_REGISTRY ??
    '0x28c154CbdeaecbF5f72B6aE48535ab9A431a4161',
  /** StonkBroker6551Account TBA implementation (mainnet, reference) */
  tbaImplementation:
    env.VITE_TBA_IMPLEMENTATION ??
    '0xE946075125843aAdb5e40e59f513d929AF507C4B',
  /** Shared Stock token implementation behind BeaconProxies (mainnet) */
  stockImplementation:
    env.VITE_STOCK_IMPLEMENTATION ??
    '0xb35490d6f9163DE4F80d88dc75c3516eb64C5aE2',
} as const

export const LINKS = {
  stockTokenTerms: 'https://robinhood.com/stocktoken/rhj',
  robinhoodCdnIconPrefix: 'https://cdn.robinhood.com',
  xProfile: 'https://x.com/AgenticStonker',
} as const

/** X (Twitter) handle the agent publishes to. */
export const X_HANDLE = '@AgenticStonker'

/**
 * The bound Agentic Stonker NFT. Artwork + ERC-721 metadata are served
 * statically by this site (public/nft/). The token-bound account (TBA)
 * address is env-provided once the account is created on-chain — never
 * invented client-side.
 */
export const NFT = {
  collection: ADDRESSES.stonkBrokersNft,
  tokenId: env.VITE_NFT_TOKEN_ID ?? '1',
  imagePath: '/nft/agentic-stonker.png',
  metadataPath: '/nft/AgenticStonker.json',
  tbaAddress: env.VITE_AGENT_TBA ?? null,
} as const

/** On-chain history for Robinhood Chain stock tokens begins here. */
export const CHAIN_HISTORY_START = '2026-07-01'

export function explorerAddressUrl(address: string, testnet = false): string {
  const base = testnet ? CHAIN.testnet.explorerUrl : CHAIN.mainnet.explorerUrl
  return `${base}/address/${address}`
}

export function explorerTxUrl(txHash: string, testnet = false): string {
  const base = testnet ? CHAIN.testnet.explorerUrl : CHAIN.mainnet.explorerUrl
  return `${base}/tx/${txHash}`
}

export function explorerTokenUrl(address: string, testnet = false): string {
  const base = testnet ? CHAIN.testnet.explorerUrl : CHAIN.mainnet.explorerUrl
  return `${base}/token/${address}`
}
